import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { Command, CommandEmpty, CommandInput, CommandList, CommandLoading } from "@/components/flexkit/Command";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import { WorkflowCard } from "@/components/automations/AutomationCard";
import { WorkflowStatusFilter, WorkflowStatusFilterValue } from "@/components/automations/AutomationStatusFilter";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useListWorkflowExecutions, useListWorkflows, type PlainWorkflow } from "@/queries/automation-queries";
import { isWorkflowRunning, isWorkflowCompleted, workflowDetailsUrl } from "@/utils/automations";
import { Command as CommandPrimitive } from "cmdk";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

type WorkflowListProps = {
    onCreateWorkflow: () => void;
};

type WorkflowListItemProps = {
    workflow: PlainWorkflow;
    statusFilter: WorkflowStatusFilterValue;
    onSelect: () => void;
};

const WorkflowListItem: FC<WorkflowListItemProps> = ({ workflow, statusFilter, onSelect }) => {
    const { data: executions } = useListWorkflowExecutions({
        enabled: true,
        workflowId: workflow.id,
    });

    const shouldShow = useMemo(() => {
        if (statusFilter === WorkflowStatusFilterValue.All) {
            return true;
        }
        if (statusFilter === WorkflowStatusFilterValue.Running) {
            return isWorkflowRunning(executions);
        }
        if (statusFilter === WorkflowStatusFilterValue.Completed) {
            return isWorkflowCompleted(executions);
        }
        return true;
    }, [statusFilter, executions]);

    if (!shouldShow) {
        return null;
    }

    return (
        <CommandPrimitive.Item
            key={workflow.id}
            value={workflow.id}
            onSelect={onSelect}
            className="group"
            keywords={[workflow.id, workflow.metadata?.name || "", workflow.metadata?.description || ""]}
        >
            <WorkflowCard
                workflow={workflow}
                className="hover:cursor-pointer group-data-[selected=true]:bg-surface-hover"
            />
        </CommandPrimitive.Item>
    );
};

export const WorkflowList: FC<WorkflowListProps> = ({ onCreateWorkflow }) => {
    const commandInputRef = useRef<React.ElementRef<typeof CommandPrimitive.Input>>(null);
    const [searchValue, setSearchValue] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<WorkflowStatusFilterValue>(WorkflowStatusFilterValue.All);
    const navigate = useNavigate();
    const { isMobileViewport: isMobile, isPending: isPendingMobileViewport } = useIsMobileViewport();

    const { data: workflows, error: workflowsError, isPending: isPendingWorkflows } = useListWorkflows();

    useEffect(() => {
        // deliberately not using autoFocus on CommandInput as we don't know if we want
        // to auto focus until we've resolved if we're on mobile or not.
        if (!isMobile && !isPendingMobileViewport) {
            commandInputRef.current?.focus();
        }
    }, [isMobile, isPendingMobileViewport]);

    const showEmptyState = !searchValue && !isPendingWorkflows && !workflowsError && workflows?.length === 0;

    if (showEmptyState) {
        return (
            <EmptyStateCard
                title="Create your first automation"
                description="Automations let you define steps once and run them across many projects or repositories. Set a trigger, add your steps, and let Ona handle the rest."
                data-testid="workflows-empty-state"
            >
                <Button
                    variant="primary"
                    LeadingIcon={IconPlus}
                    onClick={onCreateWorkflow}
                    data-tracking-id="create-workflow-empty-state"
                >
                    New Automation
                </Button>
            </EmptyStateCard>
        );
    }

    return (
        <Command
            className="flex-col gap-3 overflow-visible border-none bg-transparent shadow-none"
            data-testid="workflows-list"
        >
            <div className="flex items-center justify-between gap-3">
                <CommandInput
                    placeholder="Search automations"
                    value={searchValue}
                    ref={commandInputRef}
                    onValueChange={setSearchValue}
                    containerClassName={cn(
                        standaloneInputClassName,
                        "max-w-none *:p-0",
                        // read-only: properties only properly work on inputs. If provided elsewhere, they always apply
                        "read-only:bg-inherit read-only:cursor-auto read-only:text-inherit",
                    )}
                />
                <WorkflowStatusFilter value={statusFilter} onChange={setStatusFilter} />
            </div>
            <CommandList defaultChecked={false} className="*:flex *:flex-col *:gap-2">
                {!isPendingWorkflows && (
                    <CommandEmpty className="w-full text-center text-base text-content-muted">
                        No automations found
                        {searchValue && (
                            <>
                                {" "}
                                matching <span className="font-medium text-content-strong">{searchValue}</span>
                            </>
                        )}
                    </CommandEmpty>
                )}
                {isPendingWorkflows && (
                    <CommandLoading className="w-full text-center text-base text-content-muted">
                        Loading workflows
                    </CommandLoading>
                )}
                <ErrorMessage error={workflowsError} className="w-full text-center" />
                {(workflows || []).map((workflow) => (
                    <WorkflowListItem
                        key={workflow.id}
                        workflow={workflow}
                        statusFilter={statusFilter}
                        onSelect={() => navigate(workflowDetailsUrl(workflow))}
                    />
                ))}
            </CommandList>
        </Command>
    );
};
